import React, { useState, useEffect } from 'react';
import {
    Container, Header, Box, SpaceBetween, Button, Table, Modal, FormField,
    Input, Alert
} from '@amzn/awsui-components-react';
import { useSelector } from 'react-redux';
import CustomSelectMultiAlias from '../Templates/components/CustomSelectMultiAlias';
import { useSuggestedLogins } from 'src/hooks/useSuggestedLogins';
import {
    createAdhocTask, listAdhocTasks, deleteAdhocTask, confirmAdhocTask
} from './api';

// AdHoc tasks: dashboard-level, confirmation-only deliverables (no upload - the
// uploader just confirms the task is done). Reviewers are shown for visibility.
// Props: entityId (required) + entityType (the adhoc_entity_types name, e.g.
// 'DASHBOARD' | 'APP'; defaults to 'DASHBOARD'). The task is associated with
// (entityType, entityId).
export default (props) => {
    const entityId = props.entityId;
    const entityType = props.entityType || 'DASHBOARD';
    const userId = useSelector((s) => s.auth.userId);
    const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Create-form state
    const [taskName, setTaskName] = useState('');
    const [description, setDescription] = useState('');
    const [uploaders, setUploaders] = useState([]);
    // Reviewers/approvers are two separate alias lists; the level (column_index)
    // is assigned automatically on save (reviewer = 0, approver = 1) so users
    // never type an index.
    const [reviewers, setReviewers] = useState([]);
    const [approvers, setApprovers] = useState([]);
    const [saving, setSaving] = useState(false);
    // CustomSelectMultiAlias seeds its internal rows from getValues only once on
    // mount and doesn't re-read parent state. Bumping this key remounts the create
    // form so cleared uploaders/reviewers/approvers actually reset after a save.
    const [formKey, setFormKey] = useState(0);

    // Confirm-modal state
    const [confirmTask, setConfirmTask] = useState(null);
    const [quarter, setQuarter] = useState(props.quarter || '');
    const [trackingType, setTrackingType] = useState('PreWB');
    const [confirming, setConfirming] = useState(false);

    useEffect(() => { loadTasks(); }, [entityId, entityType]);

    const loadTasks = async () => {
        if (!entityId) return;
        setLoading(true); setError('');
        try {
            const data = await listAdhocTasks(entityType, entityId);
            // Surface a backend structured { error } instead of silently showing
            // an empty table (consistent with create/delete/confirm).
            if (data && data.error) { setError(data.error); return; }
            setTasks(Array.isArray(data) ? data : []);
        } catch (e) {
            setError('Could not load AdHoc tasks');
        } finally {
            setLoading(false);
        }
    };

    // CustomSelectMultiAlias adapters (field-based get/update).
    const SETTERS = { uploaders: setUploaders, reviewers: setReviewers, approvers: setApprovers };
    const getValues = (field) =>
        (field === 'uploaders' ? uploaders : field === 'approvers' ? approvers : reviewers);
    const updateValue = (field, value) => SETTERS[field]?.(value);

    const addTask = async () => {
        setSaving(true); setError('');
        const input = {
            task_name: taskName.trim(),
            entity_type: entityType,
            entity_id: `${entityId}`,
            description: description.trim(),
            created_by: userId,
            uploaders: uploaders.map((a) => ({ alias: a, type: 'UPLOADER' })),
            // Level is assigned automatically: reviewers = 0, approvers = 1.
            reviewers: [
                ...reviewers.map((a) => ({ alias: a, type: 'APPROVER', column_index: 0 })),
                ...approvers.map((a) => ({ alias: a, type: 'APPROVER', column_index: 1 })),
            ],
        };
        try {
            const res = await createAdhocTask(input);
            // The API resolves with the payload even when the backend returns a
            // structured { error } (authz / validation) - surface it instead of
            // silently treating it as success.
            if (res && res.error) { setError(res.error); return; }
            setTaskName(''); setDescription(''); setUploaders([]); setReviewers([]); setApprovers([]);
            setFormKey((k) => k + 1); // remount the alias inputs so they re-read cleared state
            await loadTasks();
        } catch (e) {
            setError('Could not create AdHoc task');
        } finally {
            setSaving(false);
        }
    };

    const removeTask = async (task_id) => {
        setLoading(true); setError('');
        try {
            const res = await deleteAdhocTask(task_id);
            if (res && res.error) { setLoading(false); setError(res.error); return; }
            await loadTasks();
        } catch (e) {
            setLoading(false); setError('Could not delete task');
        }
    };

    const submitConfirm = async () => {
        setConfirming(true); setError('');
        try {
            const res = await confirmAdhocTask({
                task_id: `${confirmTask.task_id}`,
                entity_type: entityType,
                entity_id: `${entityId}`,
                quarter: quarter.trim(),
                tracking_type: trackingType,
                confirmed_by: userId,
                task_name: confirmTask.task_name,
            });
            if (res && res.error) { setError(res.error); return; }
            setConfirmTask(null);
            await loadTasks();
        } catch (e) {
            setError('Could not confirm task');
        } finally {
            setConfirming(false);
        }
    };

    const aliasList = (arr) => (arr || []).map((a) => a.alias || a).join(', ');
    // Reviewers/approvers carry a level in column_index (0 = reviewer, 1 = approver);
    // render the role name rather than the raw level.
    const roleLabel = (ci) => (Number(ci) === 1 ? 'Approver' : 'Reviewer');
    const reviewerList = (arr) => (arr || [])
        .slice()
        .sort((a, b) => (Number(a?.column_index) || 0) - (Number(b?.column_index) || 0))
        .map((r) => `${r.alias} (${roleLabel(r?.column_index)})`)
        .join(', ');

    return (
        <Container header={<Header variant="h2"
            actions={<Button iconName="refresh" variant="icon" onClick={loadTasks} />}
            description="Dashboard tasks that only require a completion confirmation (no upload)">
            AdHoc tasks
        </Header>}>
            <SpaceBetween size="l">
                {error ? <Alert type="error">{error}</Alert> : null}

                {/* Create */}
                <Box key={formKey}>
                    <SpaceBetween size="s">
                        <FormField label="Task name">
                            <Input value={taskName} placeholder="e.g. Confirm quarterly reconciliation reviewed"
                                onChange={({ detail }) => setTaskName(detail.value)} />
                        </FormField>
                        <FormField label="Description">
                            <Input value={description} onChange={({ detail }) => setDescription(detail.value)} />
                        </FormField>
                        <FormField label="Uploaders (confirm the task)">
                            <CustomSelectMultiAlias field="uploaders" getValues={getValues} updateValue={updateValue}
                                options={suggestedLogins} getOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} />
                        </FormField>
                        <FormField label="Reviewers">
                            <CustomSelectMultiAlias field="reviewers" getValues={getValues} updateValue={updateValue}
                                options={suggestedLogins} getOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} />
                        </FormField>
                        <FormField label="Approvers">
                            <CustomSelectMultiAlias field="approvers" getValues={getValues} updateValue={updateValue}
                                options={suggestedLogins} getOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} />
                        </FormField>
                        <Box float="right">
                            <Button variant="primary" loading={saving}
                                disabled={!taskName.trim() || uploaders.length === 0}
                                onClick={addTask}>Add task</Button>
                        </Box>
                    </SpaceBetween>
                </Box>

                {/* List */}
                <Table
                    loading={loading}
                    loadingText="Loading tasks..."
                    items={tasks}
                    columnDefinitions={[
                        { id: 'task_name', header: 'Task', cell: (t) => t.task_name },
                        { id: 'description', header: 'Description', cell: (t) => t.description },
                        { id: 'uploaders', header: 'Uploaders', cell: (t) => aliasList(t.uploaders) },
                        { id: 'reviewers', header: 'Reviewers (by level)', cell: (t) => reviewerList(t.reviewers) },
                        {
                            id: 'actions', header: 'Actions', cell: (t) => (
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button variant="inline-link" onClick={() => { setError(''); setConfirmTask(t); }}>Mark done</Button>
                                    <Button variant="inline-link" iconName="remove" onClick={() => removeTask(t.task_id)}>Delete</Button>
                                </SpaceBetween>
                            )
                        },
                    ]}
                    empty={<Box textAlign="center">No AdHoc tasks</Box>}
                    variant="embedded"
                />
            </SpaceBetween>

            {/* Confirm modal: "are you sure this task is done? OK" */}
            <Modal
                visible={!!confirmTask}
                onDismiss={() => setConfirmTask(null)}
                header="Confirm task completion"
                footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => setConfirmTask(null)}>Cancel</Button>
                    <Button variant="primary" loading={confirming} disabled={!quarter.trim()} onClick={submitConfirm}>
                        Yes, mark done
                    </Button>
                </SpaceBetween></Box>}
            >
                <SpaceBetween size="m">
                    {error ? <Alert type="error">{error}</Alert> : null}
                    <Box>Are you sure the task <b>{confirmTask?.task_name}</b> is done?</Box>
                    <FormField label="Quarter" description="e.g. Q2 2026">
                        <Input value={quarter} onChange={({ detail }) => setQuarter(detail.value)} />
                    </FormField>
                    <FormField label="WB interval">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant={trackingType === 'PreWB' ? 'primary' : 'normal'} onClick={() => setTrackingType('PreWB')}>Pre-WB</Button>
                            <Button variant={trackingType === 'PostWB' ? 'primary' : 'normal'} onClick={() => setTrackingType('PostWB')}>Post-WB</Button>
                        </SpaceBetween>
                    </FormField>
                </SpaceBetween>
            </Modal>
        </Container>
    );
};
